/**
 * Shared types between patient and related-person schemas
 * This file exists to break circular dependencies
 */

import { RelatedPersonRelationship } from '@quromedical/fhir-common';
import { z } from 'zod';
import { DisplayReferenceSchema, UUIDSchema } from '../domain/models';
import { PaginatedBundleSchema } from '../domain/schema';
import {
  ContactGetSchema,
  ContactSchema,
  GeneralSchema,
} from '../person/schema';

// Base schemas for related person
export const BaseRelatedPersonSchema = z.object({
  general: GeneralSchema.partial(),
  contact: ContactSchema.partial(),
  patient: DisplayReferenceSchema,
  relationship: z.nativeEnum(RelatedPersonRelationship),
});

export const BaseCreateRelatedPersonSchema = BaseRelatedPersonSchema.pick({
  contact: true,
  general: true,
});

export const BasePartialRelatedPersonSchema = z.object({
  general: GeneralSchema.partial(),
  contact: ContactGetSchema.partial(),
  patient: DisplayReferenceSchema.optional(),
  relationship: z.nativeEnum(RelatedPersonRelationship).optional(),
});

export const BaseRelatedPersonGetResponseSchema =
  BasePartialRelatedPersonSchema.extend({
    id: UUIDSchema,
  });

export const BaseRelatedPersonGetAllResponseSchema =
  PaginatedBundleSchema.extend({
    relatedPersons: BaseRelatedPersonGetResponseSchema.array(),
  });

// Export types
export type BaseRelatedPerson = z.infer<typeof BaseRelatedPersonSchema>;
export type BaseCreateRelatedPerson = z.infer<
  typeof BaseCreateRelatedPersonSchema
>;
export type BasePartialRelatedPerson = z.infer<
  typeof BasePartialRelatedPersonSchema
>;
export type BaseRelatedPersonGetResponse = z.infer<
  typeof BaseRelatedPersonGetResponseSchema
>;
export type BaseRelatedPersonGetAllResponse = z.infer<
  typeof BaseRelatedPersonGetAllResponseSchema
>;
